// src/pages/Settings.js
//
// Chunk 9b — restyled to Merq Vault redesign + i18n stripped.
//
// Removed:
//   • Language section (English-only product now)
//   • "Translate categories" button + RPC call
//   • All useTranslation / i18n.changeLanguage / t() calls
//   • Emojis in chrome (👫 🚀 ⭐ 🟢 ⏳ ⚠️ 📊 🏦 📅 📎 ✨)
//
// Preserved:
//   • Paddle checkout integration (annual/monthly toggle)
//   • Partner invite flow (send / cancel / disconnect)
//   • Trial countdown logic
//   • Currency picker (still useful — supports USD/ILS/EUR/GBP/MXN)
//   • Sign out
//
import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { emitDataEvent, EVENTS } from '../lib/dataEvents'
import { useCurrency, CURRENCIES } from '../lib/CurrencyContext'
import { theme, atoms, firstName } from '../lib/theme'

const PADDLE_CLIENT_TOKEN = 'live_46c044a4190dd4a6751aa6fd9f4'
const PADDLE_MONTHLY_PRICE_ID = 'pri_01kncx4vfb468hsvas722zpbkd'
const PADDLE_ANNUAL_PRICE_ID = 'pri_01kncx0sgc4hx7j0v5q2s31anj'

// Load Paddle.js (preserved verbatim)
const loadPaddle = () => new Promise((resolve) => {
  if (window.Paddle) {
    if (!window.Paddle._initialized) {
      window.Paddle.Initialize({ token: PADDLE_CLIENT_TOKEN })
      window.Paddle._initialized = true
    }
    resolve(window.Paddle)
    return
  }
  const script = document.createElement('script')
  script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
  script.onload = () => {
    window.Paddle.Initialize({ token: PADDLE_CLIENT_TOKEN })
    window.Paddle._initialized = true
    resolve(window.Paddle)
  }
  document.head.appendChild(script)
})

export default function Settings() {
  const { setCurrency } = useCurrency()
  const [paymentMethods, setPaymentMethods] = useState([])
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null)
  useEffect(() => {
    fetchPaymentMethods()
  }, []) // eslint-disable-line

  const fetchPaymentMethods = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data } = await supabase.from('payment_methods')
      .select('*').eq('user_id', user.id).order('created_at')
    setPaymentMethods(data || [])
  }
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const [profile, setProfile] = useState(null)
  const [selectedCurrency, setSelectedCurrency] = useState('USD')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [partnerEmail, setPartnerEmail] = useState('')
  const [partnerInvites, setPartnerInvites] = useState([])
  const [sendingInvite, setSendingInvite] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [partnerLinked, setPartnerLinked] = useState(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [billingPeriod, setBillingPeriod] = useState('annual')
  // Change password (Chunk 9)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwSaved, setPwSaved] = useState(false)

  const clearPwForm = () => {
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPwError('')
  }

  const handleChangePassword = async () => {
    setPwError('')
    if (newPassword.length < 6) { setPwError('New password must be at least 6 characters.'); return }
    if (newPassword !== confirmPassword) { setPwError('New password and confirmation don’t match.'); return }
    setPwSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    // Verify the current password before changing it.
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: user?.email, password: currentPassword,
    })
    if (signInErr) { setPwSaving(false); setPwError('Current password is incorrect.'); return }
    const { error: updErr } = await supabase.auth.updateUser({ password: newPassword })
    setPwSaving(false)
    if (updErr) { setPwError(updErr.message || 'Could not update password.'); return }
    clearPwForm()
    setPwSaved(true)
    setTimeout(() => setPwSaved(false), 3000)
  }

  useEffect(() => {
    fetchProfile()
    fetchPartnerInfo()
    loadPaddle()
  }, [])

  const fetchProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single()
    if (data) { setProfile(data); setSelectedCurrency(data.currency_code || 'USD') }
  }

  const fetchPartnerInfo = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data: invites } = await supabase.from('partner_invites').select('*')
      .eq('inviter_id', user.id).order('created_at', { ascending: false })
    if (invites) setPartnerInvites(invites)
    const { data: links } = await supabase.from('partner_links')
      .select('*, user_id_1:profiles!partner_links_user_id_1_fkey(full_name, email), user_id_2:profiles!partner_links_user_id_2_fkey(full_name, email)')
      .or(`user_id_1.eq.${user.id},user_id_2.eq.${user.id}`).eq('is_active', true)
    if (links && links.length > 0) setPartnerLinked(links[0])
  }

  const handleUpgrade = async () => {
    setCheckoutLoading(true)
    try {
      const paddle = await loadPaddle()
      const { data: { user } } = await supabase.auth.getUser()
      const priceId = billingPeriod === 'annual' ? PADDLE_ANNUAL_PRICE_ID : PADDLE_MONTHLY_PRICE_ID
      paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customer: { email: user.email },
        customData: { user_id: user.id },
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          successUrl: `${window.location.origin}/settings?upgraded=true`,
        },
      })
    } catch (err) {
      console.error('Paddle checkout error:', err)
      alert('Something went wrong opening checkout. Please try again.')
    }
    setCheckoutLoading(false)
  }

  // Handle return from successful checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('upgraded') === 'true') {
      fetchProfile()
      window.history.replaceState({}, '', '/settings')
    }
  }, [])

  const handleSendInvite = async () => {
    if (!partnerEmail) return
    setSendingInvite(true)
    const { data: { user } } = await supabase.auth.getUser()
    const { data: inviteData, error } = await supabase.from('partner_invites')
      .insert({ inviter_id: user.id, invitee_email: partnerEmail }).select().single()
    if (!error && inviteData) {
      await supabase.functions.invoke('send-partner-invite', {
        body: {
          invitee_email: partnerEmail,
          inviter_name: profile?.full_name || 'Your partner',
          invite_token: inviteData.token,
        },
      })
      setInviteSent(true); setPartnerEmail(''); fetchPartnerInfo()
      setTimeout(() => setInviteSent(false), 4000)
    }
    setSendingInvite(false)
  }

  const handleRemovePartner = async () => {
    if (!window.confirm('Remove your partner connection? You will both lose access to shared data.')) return
    const { data: { user } } = await supabase.auth.getUser()
    const { data: profileData } = await supabase.from('profiles').select('partner_id').eq('id', user.id).single()
    const partnerId = profileData?.partner_id
    await supabase.from('partner_links').update({ is_active: false }).or(`user_id_1.eq.${user.id},user_id_2.eq.${user.id}`)
    await supabase.from('profiles').update({ partner_id: null }).eq('id', user.id)
    if (partnerId) await supabase.from('profiles').update({ partner_id: null }).eq('id', partnerId)
    await supabase.from('partner_links').update({ status: 'cancelled' }).eq('inviter_id', user.id).eq('status', 'pending')
    emitDataEvent(EVENTS.PARTNER_UNLINKED)
    setPartnerLinked(null); fetchPartnerInfo()
  }

  const handleCancelInvite = async (inviteId) => {
    await supabase.from('partner_invites').delete().eq('id', inviteId)
    fetchPartnerInfo()
  }

  const handleCurrencyChange = async (code) => {
    setSelectedCurrency(code); setSaving(true)
    await setCurrency(code)
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const getPartnerName = () => {
    if (!partnerLinked || !profile) return ''
    const isUser1 = partnerLinked.user_id_1?.email === profile.email
    return isUser1
      ? partnerLinked.user_id_2?.full_name || partnerLinked.user_id_2?.email
      : partnerLinked.user_id_1?.full_name || partnerLinked.user_id_1?.email
  }

  const isPro = profile?.subscription_status === 'pro' || profile?.subscription_status === 'active'
  const isTrial = !isPro

  const getTrialDaysLeft = () => {
    if (!profile?.created_at) return 45
    const created = new Date(profile.created_at)
    const now = new Date()
    const daysUsed = Math.floor((now - created) / (1000 * 60 * 60 * 24))
    return Math.max(0, 45 - daysUsed)
  }
  const trialDaysLeft = getTrialDaysLeft()
  const trialUrgent = trialDaysLeft <= 7

  const partnerName = getPartnerName()
  const partnerFirst = firstName(partnerName)

  return (
    <div style={isMobile ? styles.pageMobile : styles.page}>

      {/* Header */}
      <div style={isMobile ? styles.headerMobile : styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.pageTitle}>Settings</h1>
          <div style={atoms.pageMeta}>
            <span>Profile, subscription, and partner</span>
          </div>
        </div>
      </div>

      {/* Trial banner */}
      {isTrial && trialDaysLeft <= 14 && (
        <div style={{
          ...styles.trialBanner,
          ...(trialUrgent ? styles.trialBannerUrgent : styles.trialBannerSoft),
        }}>
          <div>
            <div style={styles.trialBannerTitle}>
              {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left in your trial
            </div>
            <div style={styles.trialBannerText}>
              Upgrade to keep full access to all features and your financial history.
            </div>
          </div>
        </div>
      )}

      {/* Profile */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Profile</div>
        <div style={styles.profileRow}>
          <div style={styles.avatar}>
            {profile?.full_name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.profileName}>{profile?.full_name || 'You'}</div>
            <div style={styles.profileEmail}>{profile?.email}</div>
            <div style={styles.profileBadge}>
              {isPro
                ? 'Pro member'
                : `Free trial — ${trialDaysLeft} days remaining`}
            </div>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Change password</div>
        <div style={styles.pwForm}>
          <input
            style={styles.pwInput}
            type="password"
            placeholder="Current password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            aria-label="Current password"
          />
          <input
            style={styles.pwInput}
            type="password"
            placeholder="New password (at least 6 characters)"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            aria-label="New password"
          />
          <input
            style={styles.pwInput}
            type="password"
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            aria-label="Confirm new password"
          />
        </div>
        {pwError && <div style={styles.pwError}>{pwError}</div>}
        {pwSaved && <div style={styles.inviteSentMsg}>Password updated</div>}
        <div style={styles.pwActions}>
          <button
            type="button"
            style={styles.pwCancel}
            onClick={clearPwForm}
            disabled={pwSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            style={{
              ...styles.pwSave,
              opacity: (pwSaving || !currentPassword || !newPassword || !confirmPassword) ? 0.5 : 1,
              cursor: (pwSaving || !currentPassword || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer',
            }}
            onClick={handleChangePassword}
            disabled={pwSaving || !currentPassword || !newPassword || !confirmPassword}
          >
            {pwSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Subscription — upgrade card (trial) */}
      {isTrial && (
        <div style={{ ...styles.card, ...styles.cardEmphasis }}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitleLarge}>Upgrade to Pro</h2>
              <p style={styles.cardSub}>
                Unlimited access for you and your partner. One subscription, two people.
              </p>
            </div>
          </div>

          {/* Billing toggle */}
          <div style={styles.billingToggle}>
            <button
              type="button" className="btn-compact"
              style={{
                ...styles.billingBtn,
                ...(billingPeriod === 'annual' ? styles.billingBtnActive : {}),
              }}
              onClick={() => setBillingPeriod('annual')}
            >
              <div style={styles.billingTitle}>Annual</div>
              <div style={styles.billingPrice}>$59</div>
              <div style={styles.billingSub}>per year · save 30%</div>
              {billingPeriod === 'annual' && (
                <div style={styles.bestValueBadge}>Best value</div>
              )}
            </button>
            <button
              type="button" className="btn-compact"
              style={{
                ...styles.billingBtn,
                ...(billingPeriod === 'monthly' ? styles.billingBtnActive : {}),
              }}
              onClick={() => setBillingPeriod('monthly')}
            >
              <div style={styles.billingTitle}>Monthly</div>
              <div style={styles.billingPrice}>$7</div>
              <div style={styles.billingSub}>per month · cancel anytime</div>
            </button>
          </div>

          <div style={styles.proFeatures}>
            {[
              'Covers you and your partner',
              'Unlimited receipt storage',
              'Full budgeting & net worth',
              'Priority support',
              'New features first',
            ].map(f => (
              <div key={f} style={styles.proFeature}>
                <span>{f}</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            style={{
              ...styles.upgradeBtn,
              opacity: checkoutLoading ? 0.7 : 1,
              cursor: checkoutLoading ? 'not-allowed' : 'pointer',
            }}
            onClick={handleUpgrade}
            disabled={checkoutLoading}
          >
            {checkoutLoading
              ? 'Opening checkout…'
              : `Upgrade — ${billingPeriod === 'annual' ? '$59 / year' : '$7 / month'}`}
          </button>
          <div style={styles.upgradeNote}>
            Secure checkout by Paddle · Cancel anytime · No hidden fees
          </div>
        </div>
      )}

      {/* Subscription — Pro active */}
      {isPro && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Subscription</div>
          <div style={styles.proStatus}>
            <span>Pro — Active</span>
          </div>
          <p style={styles.subStatusNote}>
            To manage your subscription or cancel, contact us at{' '}
            <a href="mailto:hello@getmerq.com" style={styles.subStatusLink}>
              hello@getmerq.com
            </a>
          </p>
        </div>
      )}

      {/* Partner */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <h2 style={styles.cardTitleLarge}>Shared finances</h2>
            <p style={styles.cardSub}>
              {partnerLinked
                ? `You're sharing all finances with ${partnerFirst || 'your partner'}.`
                : 'Invite your partner or spouse to share your plan, bills, net worth, and goals.'}
            </p>
          </div>
        </div>

        {partnerLinked ? (
          <>
            <div style={styles.partnerLinked}>
              <div style={styles.partnerAvatar}>
                {partnerName?.charAt(0).toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.partnerName}>{partnerName}</div>
                <div style={styles.partnerStatus}>
                  <span>Connected</span>
                </div>
              </div>
            </div>
            <div style={styles.partnerDetails}>
              {[
                'Shared plan and spending',
                'Combined net worth',
                'Shared bills and goals',
              ].map(item => (
                <div key={item} style={styles.partnerDetail}>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              style={styles.removePartnerBtn}
              onClick={handleRemovePartner}
            >
              Disconnect partner
            </button>
          </>
        ) : (
          <>
            <div style={styles.inviteRow}>
              <input
                style={styles.input}
                type="email"
                placeholder="Partner's email address"
                value={partnerEmail}
                onChange={(e) => setPartnerEmail(e.target.value)}
                aria-label="Partner email"
              />
              <button
                type="button"
                style={{
                  ...styles.inviteBtn,
                  opacity: (!partnerEmail || sendingInvite) ? 0.5 : 1,
                  cursor: (!partnerEmail || sendingInvite) ? 'not-allowed' : 'pointer',
                }}
                onClick={handleSendInvite}
                disabled={sendingInvite || !partnerEmail}
              >
                {sendingInvite ? 'Sending…' : 'Send invite'}
              </button>
            </div>
            {inviteSent && (
              <div style={styles.inviteSentMsg}>
                <span>Invite sent</span>
              </div>
            )}
            {partnerInvites.length > 0 && (
              <div style={styles.pendingInvites}>
                <div style={styles.pendingTitle}>Pending invites</div>
                {partnerInvites.map(invite => (
                  <div key={invite.id} style={styles.pendingRow}>
                    <div style={styles.pendingEmail}>{invite.invitee_email}</div>
                    <div style={{
                      ...styles.pendingStatus,
                      color: invite.status === 'accepted' ? theme.colors.green : theme.colors.orange,
                    }}>
                      {invite.status === 'accepted' ? 'Accepted' : 'Pending'}
                    </div>
                    {invite.status === 'pending' && (
                      <button
                        type="button" className="btn-compact"
                        style={styles.cancelInviteBtn}
                        onClick={() => handleCancelInvite(invite.id)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Currency */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <h2 style={styles.cardTitleLarge}>Currency</h2>
            <p style={styles.cardSub}>
              Used for all amounts shown in the app.
            </p>
          </div>
        </div>
        <div style={styles.currencyGrid}>
          {CURRENCIES.map(curr => {
            const active = selectedCurrency === curr.code
            return (
              <button
                key={curr.code}
                type="button" className="btn-compact"
                onClick={() => handleCurrencyChange(curr.code)}
                style={{
                  ...styles.currencyBtn,
                  ...(active ? styles.currencyBtnActive : {}),
                }}
              >
                <span style={styles.currencySymbol}>{curr.symbol}</span>
                <span style={styles.currencyCode}>{curr.code}</span>
                <span style={styles.currencyLabel}>{curr.label}</span>
                {active && (
                  <span aria-hidden="true" style={{ ...styles.currencyCheck, fontSize: '12px', lineHeight: 1 }}>✓</span>
                )}
              </button>
            )
          })}
        </div>
        {saving && <div style={styles.savingMsg}>Saving…</div>}
        {saved && (
          <div style={styles.savedMsg}>
            <span>Currency updated</span>
          </div>
        )}
      </div>
        {/* Payment methods */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <h2 style={styles.cardTitleLarge}>Payment methods</h2>
            <p style={styles.cardSub}>
              Cards, bank accounts, and cash you use. These show up when adding transactions.
            </p>
          </div>
        </div>
        <div style={styles.pmList}>
          {paymentMethods.length === 0 && (
            <div style={styles.pmEmpty}>
              No payment methods yet. Add your first one below.
            </div>
          )}
          {paymentMethods.map(pm => (
            <div key={pm.id} style={styles.pmRow}>
              <div style={styles.pmRowLeft}>
                <div>
                  <div style={styles.pmName}>{pm.name}</div>
                  <div style={styles.pmMeta}>
                    {pm.type === 'credit' ? 'Credit card'
                      : pm.type === 'debit' ? 'Debit card'
                      : pm.type === 'bank' ? 'Bank account'
                      : pm.type === 'cash' ? 'Cash'
                      : pm.type}
                    {pm.last_four ? ` ·· ${pm.last_four}` : ''}
                  </div>
                </div>
              </div>
              <div style={styles.pmRowActions}>
                <button
                  type="button"
                  style={styles.pmIconBtn}
                  onClick={() => setEditingPaymentMethod(pm)}
                  aria-label="Edit payment method"
                >
                  Edit
               </button>
                <button
                  type="button"
                  style={{ ...styles.pmIconBtn, color: '#BE185D' }}
                  onClick={async () => {
                    if (!window.confirm(`Remove "${pm.name}"?`)) return
                    await supabase.from('payment_methods').delete().eq('id', pm.id)
                    emitDataEvent(EVENTS.PAYMENT_METHOD_DELETED)
                    fetchPaymentMethods()
                  }}
                  aria-label="Delete payment method"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          style={styles.pmAddBtn}
          onClick={() => setEditingPaymentMethod('new')}
        >
          Add payment method
        </button>
      </div>

      {/* Edit/Add payment method sheet */}
      {editingPaymentMethod && (
        <PaymentMethodSheet
          pm={editingPaymentMethod === 'new' ? null : editingPaymentMethod}
          onClose={() => setEditingPaymentMethod(null)}
          onSaved={() => {
            setEditingPaymentMethod(null)
            fetchPaymentMethods()
          }}
        />
      )}
      {/* Account / Sign out */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Account</div>
        <button
          type="button"
          style={styles.logoutBtn}
          onClick={async () => {
            await supabase.auth.signOut()
            window.location.href = '/login'
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts
// ─── Payment method add/edit sheet ──────────────────────────
function PaymentMethodSheet({ pm, onClose, onSaved }) {
  const isEdit = !!pm
  const [name, setName] = useState(pm?.name || '')
  const [type, setType] = useState(pm?.type || 'credit')
  const [lastFour, setLastFour] = useState(pm?.last_four || '')
  const [saving, setSaving] = useState(false)

  const TYPES = [
    { value: 'credit', label: 'Credit card' },
    { value: 'debit',  label: 'Debit card' },
    { value: 'bank',   label: 'Bank account' },
    { value: 'cash',   label: 'Cash' },
  ]

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const data = {
      name: name.trim(),
      type,
      last_four: lastFour.trim() || null,
    }
    if (isEdit) {
      await supabase.from('payment_methods').update(data).eq('id', pm.id)
      emitDataEvent(EVENTS.PAYMENT_METHOD_SAVED)
    } else {
      await supabase.from('payment_methods').insert({ ...data, user_id: user.id })
      emitDataEvent(EVENTS.PAYMENT_METHOD_SAVED)
    }
    setSaving(false)
    onSaved()
  }

  return (
    <div style={pmSheetStyles.overlay} onClick={onClose}>
      <div style={pmSheetStyles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={pmSheetStyles.header}>
          <h2 style={pmSheetStyles.title}>
            {isEdit ? 'Edit payment method' : 'Add payment method'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={pmSheetStyles.closeBtn}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div style={pmSheetStyles.body}>
          <label style={pmSheetStyles.fieldLabel}>Name</label>
          <input
            style={pmSheetStyles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Chase Sapphire, Wells Fargo Checking"
            autoFocus
          />
          <label style={pmSheetStyles.fieldLabel}>Type</label>
          <div style={pmSheetStyles.typeGrid}>
            {TYPES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                style={{
                  ...pmSheetStyles.typeBtn,
                  ...(type === t.value ? pmSheetStyles.typeBtnActive : {}),
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {(type === 'credit' || type === 'debit' || type === 'bank') && (
            <>
              <label style={pmSheetStyles.fieldLabel}>Last 4 digits (optional)</label>
              <input
                style={pmSheetStyles.input}
                value={lastFour}
                onChange={(e) => setLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1234"
                maxLength={4}
              />
            </>
          )}
        </div>
        <div style={pmSheetStyles.footer}>
          <button
            type="button"
            onClick={onClose}
            style={pmSheetStyles.cancelBtn}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            style={{
              ...pmSheetStyles.saveBtn,
              opacity: (saving || !name.trim()) ? 0.5 : 1,
              cursor: (saving || !name.trim()) ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  )
}

const pmSheetStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 1000,
  },
  sheet: {
    background: '#FFFFFF',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 24px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #EDE7DA',
  },
  title: {
    fontFamily: 'Fraunces, serif',
    fontSize: '20px',
    fontWeight: 600,
    color: '#1E1040',
    margin: 0,
  },
  closeBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'transparent',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#666',
  },
  body: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
  },
  fieldLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginTop: '14px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    background: '#FFFFFF',
    border: '1px solid #D6CDB8',
    borderRadius: '10px',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: '14.5px',
    color: '#1E1040',
    outline: 'none',
    boxSizing: 'border-box',
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
  },
  typeBtn: {
    padding: '11px',
    background: '#F5F1E8',
    border: '1px solid #EDE7DA',
    borderRadius: '10px',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: '13.5px',
    fontWeight: 500,
    color: '#666',
    cursor: 'pointer',
    transition: 'all 120ms ease',
  },
  typeBtnActive: {
    background: '#1E1040',
    color: '#FAF7F2',
    borderColor: '#1E1040',
    fontWeight: 600,
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #EDE7DA',
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '10px 18px',
    background: 'transparent',
    border: '1px solid #D6CDB8',
    borderRadius: '10px',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: '13.5px',
    fontWeight: 600,
    color: '#666',
    cursor: 'pointer',
  },
  saveBtn: {
    padding: '10px 22px',
    background: '#1E1040',
    color: '#FAF7F2',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: '13.5px',
    fontWeight: 600,
  },
}
const styles = {
  page: {
    maxWidth: '760px', margin: '0 auto', width: '100%',
    padding: '24px 32px 80px',
    fontFamily: f.body, color: c.plum,
  },
  pageMobile: {
    width: '100%', padding: '0 16px 80px',
    fontFamily: f.body, color: c.plum,
  },

  header: {
    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
    marginBottom: '20px', gap: '14px', flexWrap: 'wrap',
  },
  headerMobile: {
    padding: '14px 0',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  pageTitle: {
    fontFamily: f.display, fontWeight: 600, fontSize: '32px',
    letterSpacing: '-0.025em', color: c.plum, margin: 0,
  },

  // Trial banner
  trialBanner: {
    display: 'flex', gap: '14px', alignItems: 'flex-start',
    padding: '14px 16px',
    borderRadius: '12px', marginBottom: '16px',
    border: '1px solid',
  },
  trialBannerSoft: {
    background: c.orangeSoft,
    borderColor: 'rgba(217, 119, 6, 0.2)',
    color: c.orange,
  },
  trialBannerUrgent: {
    background: c.roseSoft,
    borderColor: 'rgba(190, 24, 93, 0.2)',
    color: c.rose,
  },
  trialBannerIcon: {
    width: '32px', height: '32px',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.5)',
    display: 'grid', placeItems: 'center',
    flexShrink: 0,
  },
  trialBannerTitle: {
    fontSize: '14px', fontWeight: 600,
    marginBottom: '2px',
  },
  trialBannerText: {
    fontSize: '12.5px', lineHeight: 1.45, opacity: 0.9,
  },

  // Card
  card: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '20px 22px',
    marginBottom: '14px',
    boxShadow: theme.shadows.card,
  },
  cardEmphasis: {
    border: `1.5px solid ${c.green}`,
    background: '#FBFBF5',
  },
  cardHeader: {
    display: 'flex', gap: '14px',
    marginBottom: '14px',
    alignItems: 'flex-start',
  },
  cardIcon: {
    width: '38px', height: '38px',
    borderRadius: '10px',
    background: c.purpleSoft, color: c.purple,
    display: 'grid', placeItems: 'center', flexShrink: 0,
  },
  cardEmphasisIcon: {
    width: '38px', height: '38px',
    borderRadius: '10px',
    background: c.greenSoft, color: c.green,
    display: 'grid', placeItems: 'center', flexShrink: 0,
  },
  cardTitle: {
    fontFamily: f.body,
    fontSize: '13px', fontWeight: 700,
    color: c.inkMuted,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    marginBottom: '14px',
  },
  cardTitleLarge: {
    fontFamily: f.body,
    fontSize: '17px', fontWeight: 600,
    color: c.plum,
    margin: '0 0 4px 0',
  },
  cardSub: {
    fontSize: '13px', color: c.inkMuted,
    lineHeight: 1.5, margin: 0,
  },

  // Profile row
  profileRow: {
    display: 'flex', alignItems: 'center', gap: '14px',
  },
  avatar: {
    width: '52px', height: '52px',
    borderRadius: '50%',
    background: c.greenSoft, color: c.green,
    display: 'grid', placeItems: 'center',
    fontFamily: f.display,
    fontSize: '22px', fontWeight: 600,
    flexShrink: 0,
  },
  profileName: {
    fontSize: '16px', fontWeight: 600,
    color: c.plum,
  },
  profileEmail: {
    fontSize: '13px', color: c.inkMuted,
    marginTop: '2px',
  },
  profileBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '12px', color: c.green,
    fontWeight: 600,
    marginTop: '6px',
  },

  // Billing toggle
  billingToggle: {
    display: 'flex', gap: '10px', marginBottom: '16px',
  },
  billingBtn: {
    flex: 1, padding: '14px',
    background: c.cream2,
    border: `1.5px solid ${c.line}`,
    borderRadius: '12px',
    cursor: 'pointer', textAlign: 'center',
    position: 'relative',
    fontFamily: f.body,
  },
  billingBtnActive: {
    border: `1.5px solid ${c.green}`,
    background: c.greenSoft,
  },
  billingTitle: {
    fontSize: '13px', fontWeight: 600,
    color: c.plum, marginBottom: '4px',
  },
  billingPrice: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '26px', color: c.plum,
    letterSpacing: '-0.025em',
    marginBottom: '2px', lineHeight: 1.05,
  },
  billingSub: {
    fontSize: '11px', color: c.inkMuted,
  },
  bestValueBadge: {
    position: 'absolute', top: '-9px', right: '10px',
    background: c.green, color: '#FFFFFF',
    fontSize: '10px', fontWeight: 700,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    padding: '3px 8px', borderRadius: '999px',
  },
  proFeatures: { marginBottom: '16px' },
  proFeature: {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontSize: '13px', color: c.plum,
    padding: '5px 0',
  },
  upgradeBtn: {
    width: '100%', padding: '13px',
    background: c.green, color: '#FFFFFF',
    border: 'none', borderRadius: '10px',
    fontFamily: f.body, fontSize: '14.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', gap: '6px',
    marginBottom: '8px',
  },
  upgradeNote: {
    fontSize: '11.5px', color: c.inkMuted,
    textAlign: 'center',
  },

  proStatus: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontSize: '14.5px', fontWeight: 600,
    color: c.green,
    background: c.greenSoft,
    padding: '6px 12px', borderRadius: '999px',
  },
  subStatusNote: {
    fontSize: '13px', color: c.inkMuted,
    marginTop: '12px', lineHeight: 1.5,
  },
  subStatusLink: {
    color: c.green, fontWeight: 600,
    textDecoration: 'none',
  },

  // Partner
  partnerLinked: {
    display: 'flex', alignItems: 'center', gap: '14px',
    marginBottom: '14px',
    padding: '12px 14px',
    background: c.cream2,
    borderRadius: '10px',
  },
  partnerAvatar: {
    width: '44px', height: '44px',
    borderRadius: '50%',
    background: c.green, color: '#FFFFFF',
    display: 'grid', placeItems: 'center',
    fontFamily: f.display,
    fontSize: '18px', fontWeight: 600,
    flexShrink: 0,
  },
  partnerName: {
    fontSize: '15px', fontWeight: 600,
    color: c.plum,
  },
  partnerStatus: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '12px', color: c.green, fontWeight: 600,
    marginTop: '2px',
  },
  partnerDetails: {
    marginBottom: '14px',
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  partnerDetail: {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontSize: '13px', color: c.plumSoft,
  },
  removePartnerBtn: {
    width: '100%', padding: '11px',
    background: 'transparent', color: c.rose,
    border: `1px solid rgba(190, 24, 93, 0.25)`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px',
  },

  inviteRow: {
    display: 'flex', gap: '8px', marginBottom: '10px',
  },
  inviteBtn: {
    padding: '10px 16px',
    background: c.green, color: '#FFFFFF',
    border: 'none', borderRadius: '8px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    flexShrink: 0,
  },
  inviteSentMsg: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: c.greenSoft, color: c.green,
    padding: '8px 12px', borderRadius: '8px',
    fontSize: '13px', fontWeight: 600,
    marginBottom: '10px',
  },
  pendingInvites: { marginTop: '14px' },
  pendingTitle: {
    fontSize: '11px', fontWeight: 700,
    color: c.inkMuted,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    marginBottom: '8px',
  },
  pendingRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 12px',
    background: c.cream2,
    borderRadius: '8px',
    marginBottom: '6px',
  },
  pendingEmail: {
    flex: 1, minWidth: 0,
    fontSize: '13px', color: c.plum,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  pendingStatus: {
    fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  cancelInviteBtn: {
    padding: '4px 10px',
    background: 'transparent', color: c.inkMuted,
    border: `1px solid ${c.line}`,
    borderRadius: '6px',
    fontFamily: f.body, fontSize: '11.5px', fontWeight: 600,
    cursor: 'pointer',
  },

  input: {
    flex: 1, minWidth: 0,
    padding: '10px 12px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, outline: 'none',
    boxSizing: 'border-box',
  },

  // Change password (Chunk 9)
  pwForm: { display: 'flex', flexDirection: 'column', gap: '10px' },
  pwInput: {
    width: '100%',
    padding: '11px 12px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '9px',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, outline: 'none',
    boxSizing: 'border-box',
  },
  pwError: {
    marginTop: '12px', fontSize: '13px', fontWeight: 500,
    color: c.rose, lineHeight: 1.5,
  },
  pwActions: {
    display: 'flex', gap: '10px', marginTop: '14px', justifyContent: 'flex-end',
  },
  pwCancel: {
    padding: '10px 16px',
    background: 'transparent', color: c.plumSoft,
    border: `1px solid ${c.lineStrong}`, borderRadius: '8px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
  },
  pwSave: {
    padding: '10px 20px',
    background: c.green, color: '#FFFFFF',
    border: 'none', borderRadius: '8px',
    fontFamily: f.body, fontSize: '13px', fontWeight: 600,
  },

  // Currency
  currencyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    gap: '8px',
  },
  currencyBtn: {
    position: 'relative',
    padding: '14px 10px',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '4px',
    fontFamily: f.body,
  },
  currencyBtnActive: {
    background: c.greenSoft,
    borderColor: 'rgba(22, 163, 74, 0.3)',
  },
  currencySymbol: {
    fontFamily: f.display,
    fontSize: '22px', fontWeight: 600,
    color: c.plum, lineHeight: 1,
    letterSpacing: '-0.02em',
  },
  currencyCode: {
    fontSize: '12px', fontWeight: 600,
    color: c.plum,
    marginTop: '2px',
  },
  currencyLabel: {
    fontSize: '10.5px', color: c.inkMuted,
    letterSpacing: '0.02em',
  },
  currencyCheck: {
    position: 'absolute', top: '8px', right: '8px',
    color: c.green,
  },
  savingMsg: {
    fontSize: '12px', color: c.inkMuted,
    marginTop: '10px',
  },
  savedMsg: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '12.5px', fontWeight: 600,
    color: c.green,
    marginTop: '10px',
  },
  // Payment methods
  pmList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '14px',
    marginBottom: '14px',
  },
  pmEmpty: {
    fontSize: '13.5px',
    color: c.inkMuted,
    fontStyle: 'italic',
    padding: '12px',
    textAlign: 'center',
  },
  pmRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
  },
  pmRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  pmRowIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: c.white,
    border: `1px solid ${c.line}`,
    display: 'grid',
    placeItems: 'center',
    color: c.plum,
    flexShrink: 0,
  },
  pmName: {
    fontSize: '14.5px',
    fontWeight: 600,
    color: c.plum,
  },
  pmMeta: {
    fontSize: '12.5px',
    color: c.inkMuted,
    marginTop: '1px',
  },
  pmRowActions: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },
  pmIconBtn: {
    padding: '6px 10px',
    borderRadius: '8px',
    background: 'transparent',
    border: 'none',
    color: c.inkMuted,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
  },
  pmAddBtn: {
    background: 'transparent',
    color: c.plum,
    border: `1px dashed ${c.lineStrong}`,
    padding: '11px 14px',
    borderRadius: '10px',
    fontFamily: f.body,
    fontSize: '13.5px',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  // Sign out
  logoutBtn: {
    width: '100%', padding: '12px',
    background: c.cream2,
    color: c.rose,
    border: `1px solid rgba(190, 24, 93, 0.2)`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px',
  },
}
